import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { Stack, Text, textVariants } from '@etg/wings';
import { useProperty, useTranslation } from '@eti/providers';
import { LoadingDots, Notification, variants } from '@eti/components';
import { css } from '@eti/styles';
import { PartOfExternalFlights } from '@eti/schema-types';
import TopicsContentWrapper from '../TopicsContentWrapper';
import OrderChangeabilityContent from '../../manage-trip/rebooking/OrderChangeabilityContent';
import { REBOOK_ELIGIBILITY } from '../../../containers/queries';
import type { RebookEligibilityType } from '../../manage-trip/rebooking/models';
import { RebookEligibility } from '../../manage-trip/rebooking/models';
import { useOrderDetails } from '../../../containers/OrderDetailsContext';
import FaqSection from '../../common/FaqSection';
import RebookingRightContent from './RebookingRightContent';

const loadingDotsStyles = css`
  font-size: 2.25rem;
  text-align: center;
`;

const Rebooking = () => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const { partOfExternalFlights, faqSectionPerSlug } = useOrderDetails();
  const faqSectionSlug = p('ContactUs.Rebooking.Faq.Slug');
  const [hasError, setHasError] = useState(false);

  const [rebookEligibilityMutation, { data, loading }] = useMutation(REBOOK_ELIGIBILITY, {
    onCompleted: () => setHasError(false),
    onError: () => setHasError(true),
  });

  useEffect(() => {
    if (partOfExternalFlights !== PartOfExternalFlights.All) {
      rebookEligibilityMutation();
    }
  }, [partOfExternalFlights, rebookEligibilityMutation]);

  if (partOfExternalFlights === PartOfExternalFlights.All) {
    return (
      <TopicsContentWrapper
        leftColumnContent={
          <Stack spacing={24}>
            <Stack spacing={8}>
              <Text variant={textVariants._16_BOLD}>
                {t('ManageTrip.Cancel.RyanairFlight.NotAllowed.Title')}
              </Text>
              <Text>{t('ManageTrip.Cancel.RyanairFlight.NotAllowed.Description')}</Text>
            </Stack>
            {faqSectionPerSlug?.[faqSectionSlug] && (
              <FaqSection
                headingAs="h5"
                headingLevel={4}
                padding={0}
                section={faqSectionPerSlug[faqSectionSlug]}
              />
            )}
          </Stack>
        }
        rightColumnContent={
          <Notification
            dataTestId="rebooking-no-contact-us-notification"
            variant={variants.WARNING}
          >
            <Text asHtml>{t('ContactUs.NoContact.Notification.Content')}</Text>
          </Notification>
        }
        shouldShowContactMethods={false}
      />
    );
  }

  if (loading) {
    return <LoadingDots containerStyle={loadingDotsStyles} />;
  }

  if (hasError) {
    return (
      <TopicsContentWrapper
        chatType="genesys"
        leftColumnContent={
          <Stack spacing={24}>
            <Notification
              dataTestId="rebooking-contact-us-error-notification"
              variant={variants.WARNING}
            >
              {t('MyBookingsWelcomePage.ErrorModal.Content')}
            </Notification>
            {faqSectionPerSlug?.[faqSectionSlug] && (
              <FaqSection
                headingAs="h5"
                headingLevel={4}
                padding={0}
                section={faqSectionPerSlug[faqSectionSlug]}
              />
            )}
          </Stack>
        }
        shouldShowContactMethods
        snowplowProperty="CHANGE"
      />
    );
  }

  const rebookFlow = data?.rebookEligibility?.rebookFlow as
    | RebookEligibilityType
    | null
    | undefined;

  if (rebookFlow) {
    const ELIGIBILITY_TYPES_SUPPORTING_CONTACT: RebookEligibilityType[] = [
      RebookEligibility.CHANGE_BY_ETG,
      RebookEligibility.CHANGE_PNR_WITH_ETG,
      RebookEligibility.CHANGE_FLEXIBLE_TICKET,
      RebookEligibility.CHANGE_BY_ETG_IF_POSSIBLE,
      RebookEligibility.CHANGE_NOT_CONFIRMED,
      RebookEligibility.CHANGE_NOT_OPEN_TICKET,
      RebookEligibility.CHANGE_UNKNOWN,
    ];
    const shouldShowContactMethods = ELIGIBILITY_TYPES_SUPPORTING_CONTACT.includes(rebookFlow);

    return (
      <TopicsContentWrapper
        chatType="genesys"
        leftColumnContent={
          <Stack spacing={24}>
            <OrderChangeabilityContent
              rebookEligibilityType={rebookFlow}
              shouldShowChangeTripButton={false}
              shouldShowContactMethods={false}
            />
            {faqSectionPerSlug?.[faqSectionSlug] && (
              <FaqSection
                headingAs="h5"
                headingLevel={4}
                padding={0}
                section={faqSectionPerSlug[faqSectionSlug]}
              />
            )}
          </Stack>
        }
        rightColumnContent={<RebookingRightContent rebookEligibilityType={rebookFlow} />}
        shouldShowContactMethods={shouldShowContactMethods}
        snowplowProperty="CHANGE"
      />
    );
  }
  return null;
};

export default Rebooking;
